import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { clientPortalAPI } from '../lib/api';
import { FileBarChart, Loader2, FileDown, Download, Instagram, Megaphone, FileText, MessageSquareQuote } from 'lucide-react';
import { ContentReportView, AdsReportView, PrintStyles, ReportPrintHeader, fmtMonth, toHtml } from './MonthlyReportViews';
import { RichTextEditor } from './RichTextEditor';

function PortalTypedReport({ rtype }) {
  const isContent = rtype === 'content';
  const [months, setMonths] = useState(null);
  const [selMonth, setSelMonth] = useState('');
  const [report, setReport] = useState(null);

  useEffect(() => {
    setMonths(null); setReport(null);
    clientPortalAPI.getTypedReports(rtype).then(async (r) => {
      const ms = r?.reports || [];
      setMonths(ms);
      if (ms.length > 0) {
        setSelMonth(ms[0].month);
        const rep = await clientPortalAPI.getTypedReport(rtype, ms[0].month).catch(() => null);
        setReport(rep);
      }
    }).catch(() => setMonths([]));
  }, [rtype]);

  const openMonth = async (m) => {
    setSelMonth(m); setReport(null);
    try { setReport(await clientPortalAPI.getTypedReport(rtype, m)); } catch { setReport(null); }
  };

  if (months === null) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>;
  if (months.length === 0) {
    return (
      <Card><CardContent className="py-14 text-center text-muted-foreground text-sm" data-testid={`portal-reports-empty-${rtype}`}>
        Aún no hay informes de {isContent ? 'contenido' : 'anuncios'} publicados.
      </CardContent></Card>
    );
  }
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2 print:hidden">
        <div className="flex items-center gap-1.5 flex-wrap">
          {months.map((m) => (
            <button key={m.month} onClick={() => openMonth(m.month)} className={`text-xs px-2.5 py-1.5 rounded-md border ${m.month === selMonth ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`} data-testid={`portal-report-month-${rtype}-${m.month}`}>
              {fmtMonth(m.month)}
            </button>
          ))}
        </div>
        {report && <Button size="sm" variant="outline" onClick={() => window.print()} data-testid={`portal-report-pdf-${rtype}`}><FileDown className="w-4 h-4 mr-1" /> Descargar PDF</Button>}
      </div>
      {!report ? (
        <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
      ) : (
        <div id="printable-report" className="space-y-5" data-testid={`portal-report-view-${rtype}`}>
          <PrintStyles />
          <ReportPrintHeader title={isContent ? 'Informe de Contenido' : 'Informe de Ads'} month={report.month} />
          {report.pdf && (
            <a href={clientPortalAPI.reportPdfUrl(rtype, report.month)} target="_blank" rel="noreferrer" className="print:hidden block" data-testid={`portal-report-pdf-file-${rtype}`}>
              <Card className="hover:border-primary transition-colors">
                <CardContent className="py-3 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    <FileText className="w-4 h-4 text-primary shrink-0" />
                    <span className="font-medium">Informe en PDF</span>
                    <span className="text-muted-foreground text-xs truncate">{report.pdf.name}</span>
                  </div>
                  <Download className="w-4 h-4 shrink-0" />
                </CardContent>
              </Card>
            </a>
          )}
          {report.analysis && (
            <Card className="border-primary/30 bg-primary/5 print-avoid">
              <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><MessageSquareQuote className="w-4 h-4 text-primary" /> Análisis de tu equipo</CardTitle></CardHeader>
              <CardContent><RichTextEditor value={toHtml(report.analysis)} editable={false} testId={`portal-report-analysis-${rtype}`} /></CardContent>
            </Card>
          )}
          {isContent ? <ContentReportView data={report.data} /> : <AdsReportView data={report.data} />}
        </div>
      )}
    </div>
  );
}

export default function ClientReportsSection() {
  const [files, setFiles] = useState([]);
  useEffect(() => {
    clientPortalAPI.getDocuments('informe').then((r) => setFiles(r.documents || [])).catch(() => {});
  }, []);
  return (
    <div className="space-y-5" data-testid="client-reports-section">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2"><FileBarChart className="w-7 h-7 text-primary" /> Informes</h1>
        <p className="text-sm text-muted-foreground mt-1">Tus informes mensuales de contenido orgánico y de campañas de anuncios.</p>
      </div>
      <Tabs defaultValue="content">
        <TabsList>
          <TabsTrigger value="content" data-testid="portal-reports-tab-content"><Instagram className="w-4 h-4 mr-1.5" /> Contenido</TabsTrigger>
          <TabsTrigger value="ads" data-testid="portal-reports-tab-ads"><Megaphone className="w-4 h-4 mr-1.5" /> Ads</TabsTrigger>
        </TabsList>
        <TabsContent value="content" className="mt-4"><PortalTypedReport rtype="content" /></TabsContent>
        <TabsContent value="ads" className="mt-4"><PortalTypedReport rtype="ads" /></TabsContent>
      </Tabs>
      {files.length > 0 && (
        <Card className="print:hidden">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-primary" /> Archivos de informes</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {files.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg border" data-testid={`portal-report-file-${d.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{d.nombre}</p>
                  <p className="text-xs text-muted-foreground">{d.mes ? `${d.mes} · ` : ''}{d.created_at ? new Date(d.created_at).toLocaleDateString('es-ES') : ''}</p>
                </div>
                <a href={clientPortalAPI.docDownloadUrl(d.id)} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline"><Download className="w-4 h-4 mr-1" /> Descargar</Button>
                </a>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
